import React from "react";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  BarChart3,
} from "lucide-react";

const LearningMomentumSection = ({ learningMomentumData }) => {
  const isImproving = learningMomentumData.momentum === "improving";

  const badgeConfig = isImproving
    ? {
        bg: "bg-emerald-50 border-emerald-200 text-emerald-700",
        icon: TrendingUp,
        label: "Improving",
      }
    : {
        bg: "bg-red-50 border-red-200 text-red-700",
        icon: TrendingDown,
        label: "Declining",
      };

  const BadgeIcon = badgeConfig.icon;

  const maxAccuracy = Math.max(
    ...(learningMomentumData.trendData?.accuracy || []),
    100,
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">
            Learning Momentum
          </h2>
          <p className="text-xs text-gray-500">Recent learning trend</p>
        </div>

        <div
          className={`flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium ${badgeConfig.bg}`}
        >
          <BadgeIcon className="w-3.5 h-3.5" />
          {badgeConfig.label}
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="border rounded-lg p-2">
          <div className="flex items-center gap-1 mb-1">
            <Target className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-[11px] text-gray-500">Previous</span>
          </div>
          <div className="text-base font-bold text-gray-900">
            {learningMomentumData.metrics?.previousAccuracy?.toFixed(1)}%
          </div>
        </div>

        <div className="border rounded-lg p-2">
          <div className="flex items-center gap-1 mb-1">
            <Activity className="w-3.5 h-3.5 text-purple-600" />
            <span className="text-[11px] text-gray-500">Recent</span>
          </div>
          <div className="text-base font-bold text-gray-900">
            {learningMomentumData.metrics?.recentAccuracy?.toFixed(1)}%
          </div>
        </div>

        <div className="border rounded-lg p-2">
          <div className="flex items-center gap-1 mb-1">
            <BarChart3
              className={`w-3.5 h-3.5 ${
                learningMomentumData.metrics?.improvement >= 0
                  ? "text-emerald-600"
                  : "text-red-600"
              }`}
            />
            <span className="text-[11px] text-gray-500">Change</span>
          </div>

          <div
            className={`text-base font-bold ${
              learningMomentumData.metrics?.improvement >= 0
                ? "text-emerald-600"
                : "text-red-600"
            }`}
          >
            {learningMomentumData.metrics?.improvement > 0 ? "+" : ""}
            {learningMomentumData.metrics?.improvement}%
          </div>
        </div>
      </div>

      {/* Mini Trend Chart */}
      <div className="border rounded-lg p-3 mb-3">
        <div className="flex items-end gap-1 h-20">
          {learningMomentumData.trendData?.accuracy.map((value, index) => (
            <div
              key={index}
              className="flex-1 flex flex-col items-center justify-end gap-1"
            >
              <div
                className={`w-full rounded-t-sm transition-all ${
                  isImproving
                    ? "bg-gradient-to-t from-emerald-500 to-emerald-400"
                    : "bg-gradient-to-t from-red-500 to-red-400"
                }`}
                style={{
                  height: `${Math.max((value / maxAccuracy) * 60, 8)}px`,
                }}
              />

              <span className="text-[10px] text-gray-500">
                {learningMomentumData.trendData?.labels[index]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Insight */}
      <div
        className={`text-xs p-2 rounded-lg border ${
          isImproving
            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
            : "bg-red-50 border-red-200 text-red-700"
        }`}
      >
        <div className="font-medium mb-0.5">{learningMomentumData.title}</div>
        <p>{learningMomentumData.description}</p>
      </div>
    </div>
  );
};

export default LearningMomentumSection;
